import React, { useEffect, useState, useCallback } from "react";
import { dms, inr, niceDate } from "./api";
import { PageHeader } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const selStyle = { padding: "8px 10px", borderRadius: 8, fontSize: 14, border: "1px solid rgba(128,128,128,0.35)", background: "transparent", color: "inherit" };

export default function CollectionsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [dists, setDists] = useState([]);
  const [f, setF] = useState({ from_date: "", to_date: "", distributor_id: "", status: "", method: "" });
  const [delRow, setDelRow] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    dms.collections(params).then((r) => { setRows(r.items || []); setTotals(r.totals || {}); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"));
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { dms.listDistributors().then((d) => setDists(d.data || [])).catch(() => {}); }, []);

  const doDelete = async () => {
    if (reason.trim().length < 3) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await dms.deleteCollection(delRow.id, reason.trim());
      toast.success("Payment deleted");
      setDelRow(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doConfirm = async (id) => {
    try { await dms.confirmPrimaryPayment(id); toast.success("Payment confirmed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Collections" subtitle="Payments received from distributors" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Today</div><div className="text-lg font-bold text-emerald-700">{inr(totals.today || 0)}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">This month</div><div className="text-lg font-bold text-emerald-700">{inr(totals.month || 0)}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Confirmed (filtered)</div><div className="text-lg font-bold">{inr(totals.confirmed || 0)}</div></Card>
        <Card className="p-4"><div className="text-[10px] uppercase text-slate-500">Pending (filtered)</div><div className="text-lg font-bold text-amber-700">{inr(totals.pending || 0)}</div></Card>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input type="date" className="w-40" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} />
        <Input type="date" className="w-40" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} />
        <select style={selStyle} value={f.distributor_id} onChange={(e) => setF({ ...f, distributor_id: e.target.value })}>
          <option value="">All distributors</option>
          {dists.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select style={selStyle} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="">All status</option><option value="confirmed">Confirmed</option><option value="pending">Pending</option>
        </select>
        <select style={selStyle} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>
          <option value="">All modes</option><option value="bank_transfer">Bank Transfer</option><option value="cash">Cash</option><option value="cheque">Cheque</option><option value="upi">UPI</option>
        </select>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Distributor</TableHead><TableHead>Mode</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-xs text-slate-500">{niceDate(e.at)}</TableCell>
                <TableCell className="font-medium">{e.distributor_name || e.distributor_id}</TableCell>
                <TableCell className="text-xs">{(e.method || "").replace("_", " ")}</TableCell>
                <TableCell className="font-mono text-xs">{e.reference_no}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded ${e.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{e.status === "pending" ? "Pending" : "Confirmed"}</span>
                </TableCell>
                <TableCell className="text-right font-semibold text-emerald-700">{inr(e.amount)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {e.status === "pending" && <Button size="sm" variant="outline" className="mr-2" onClick={() => doConfirm(e.id)}>Confirm</Button>}
                  {isOwner && <Button size="sm" variant="outline" className="text-rose-700" onClick={() => { setDelRow(e); setReason(""); }} data-testid={`delete-collection-${e.id}`}><Trash2 size={14} className="mr-1" />Delete</Button>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6">No payments found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={!!delRow} onOpenChange={(o) => { if (!o) setDelRow(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete this payment?</DialogTitle></DialogHeader>
          {delRow && (
            <div className="text-sm space-y-2">
              <div>The {inr(delRow.amount)} payment ({delRow.reference_no}) from <b>{delRow.distributor_name}</b> will be deleted. The distributor's outstanding will go back up.</div>
              <Textarea placeholder="Reason (required), e.g. marked as received by mistake" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelRow(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy} onClick={doDelete}>{busy ? "Deleting…" : "Delete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
